import React, { Fragment, useState, useRef, useEffect } from 'react';

import type { WithIntlProps, WrappedComponentProps } from 'react-intl';
import { injectIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import type { DispatchAnalyticsEvent } from '@atlaskit/editor-common/analytics/types/dispatch-analytics-event';
import {
	ACTION,
	ACTION_SUBJECT,
	EVENT_TYPE,
	TRIGGER_METHOD,
} from '@atlaskit/editor-common/analytics/types/enums';
import { findReplaceMessages as messages } from '@atlaskit/editor-common/messages/find-replace';
import { ValidMessage } from '@atlaskit/form/valid-message';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Text, xcss } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield/text-field';

import { FindReplaceTooltipButton } from './FindReplaceTooltipButton';

const SPACE_REGEX = / /u;

const replaceContainerStyles = xcss({
	padding: 'space.100',
});

const replaceWithLabelStyle = xcss({
	paddingBottom: 'space.050',
});

const actionButtonContainerStyles = xcss({
	paddingTop: 'space.200',
});

// Without the replace field above it, the button row needs no separating space.
const findOnlyActionButtonContainerStyles = xcss({
	paddingTop: 'space.0',
});

const actionButtonParentInlineStyles = xcss({
	justifyContent: 'space-between',
	flexDirection: 'row-reverse',
	flexWrap: 'wrap',
	gap: 'space.075',
});

const actionButtonInlineStyles = xcss({
	gap: 'space.075',
});

const closeButtonInlineStyles = xcss({
	marginRight: 'auto',
});

export type ReplaceProps = {
	/**
	 * When `false`, the replace label, field, replacement count message and both
	 * replace buttons are not rendered. Both chevrons and the Close button stay.
	 * Defaults to `true`.
	 */
	allowReplace?: boolean;
	canReplace: boolean;
	count: {
		index: number;
		total: number;
		totalReplaceable?: number;
	};
	dispatchAnalyticsEvent?: DispatchAnalyticsEvent;
	findTyped: boolean;
	focusToolbarButton: () => void;
	onArrowUp: () => void;
	onCancel: ({
		triggerMethod,
	}: {
		triggerMethod: TRIGGER_METHOD.KEYBOARD | TRIGGER_METHOD.TOOLBAR | TRIGGER_METHOD.BUTTON;
	}) => void;
	onFindNext: ({
		triggerMethod,
	}: {
		triggerMethod: TRIGGER_METHOD.KEYBOARD | TRIGGER_METHOD.BUTTON;
	}) => void;
	onFindPrev: ({
		triggerMethod,
	}: {
		triggerMethod: TRIGGER_METHOD.KEYBOARD | TRIGGER_METHOD.BUTTON;
	}) => void;
	onReplace: ({
		triggerMethod,
		replaceText,
	}: {
		replaceText: string;
		triggerMethod: TRIGGER_METHOD.KEYBOARD | TRIGGER_METHOD.BUTTON;
	}) => void;
	onReplaceAll: ({ replaceText }: { replaceText: string }) => void;
	onReplaceTextfieldRefSet: (ref: React.RefObject<HTMLInputElement>) => void;
	replaceText?: string;
	setFindTyped: (value: boolean) => void;
};

const Replace = ({
	allowReplace = true,
	canReplace,
	replaceText: initialReplaceText,
	onReplace,
	onReplaceAll,
	onReplaceTextfieldRefSet,
	onArrowUp,
	onCancel,
	count,
	onFindNext,
	onFindPrev,
	dispatchAnalyticsEvent,
	setFindTyped,
	findTyped,
	focusToolbarButton,
	intl: { formatMessage },
}: ReplaceProps & WrappedComponentProps) => {
	const [replaceText, setReplaceText] = useState(initialReplaceText || '');
	const [isComposing, setIsComposing] = useState(false);
	const [isHelperMessageVisible, setIsHelperMessageVisible] = useState(false);
	const [fakeSuccessReplacementMessageUpdate, setFakeSuccessReplacementMessageUpdate] =
		useState(false);
	const [replaceCount, setReplaceCount] = useState(0);

	const replaceTextfieldRef = useRef<HTMLInputElement>(null);
	const successReplacementMessageRef = useRef<HTMLDivElement>(null);

	const replaceWith = formatMessage(messages.replaceWith);
	const replaceAll = formatMessage(messages.replaceAll);
	const findPrevious = formatMessage(messages.findPrevious);
	const closeFindReplaceDialog = formatMessage(messages.closeFindReplaceDialog);

	useEffect(() => {
		onReplaceTextfieldRefSet(replaceTextfieldRef);
	}, [onReplaceTextfieldRefSet]);

	useEffect(() => {
		setReplaceText(initialReplaceText || '');
	}, [initialReplaceText]);

	const skipWhileComposing = (fn: () => void) => {
		if (!isComposing) {
			fn();
		}
	};

	const triggerSuccessReplacementMessageUpdate = (currentReplaceCount: number) => {
		if (replaceCount === currentReplaceCount) {
			setFakeSuccessReplacementMessageUpdate(!fakeSuccessReplacementMessageUpdate);
		}
		if (successReplacementMessageRef.current) {
			const ariaLiveRegion =
				successReplacementMessageRef.current.querySelector('[aria-live="polite"]');
			ariaLiveRegion?.removeAttribute('aria-live');
			ariaLiveRegion?.setAttribute('aria-live', 'polite');
		}
	};

	const handleReplaceClick = () =>
		skipWhileComposing(() => {
			onReplace({ triggerMethod: TRIGGER_METHOD.BUTTON, replaceText });
			triggerSuccessReplacementMessageUpdate(1);
			setIsHelperMessageVisible(true);
			setReplaceCount(1);
			setFindTyped(false);
		});

	const handleReplaceChange = (event: React.ChangeEvent<HTMLInputElement>) =>
		skipWhileComposing(() => {
			updateReplaceValue(event.target.value);
		});

	const updateReplaceValue = (text: string) => {
		if (dispatchAnalyticsEvent) {
			dispatchAnalyticsEvent({
				eventType: EVENT_TYPE.TRACK,
				action: ACTION.CHANGED_REPLACEMENT_TEXT,
				actionSubject: ACTION_SUBJECT.FIND_REPLACE_DIALOG,
			});
		}
		setReplaceText(text);
	};

	const handleReplaceKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) =>
		skipWhileComposing(() => {
			if (event.key === 'Enter') {
				onReplace({ triggerMethod: TRIGGER_METHOD.KEYBOARD, replaceText });
			} else if (event.key === 'ArrowUp') {
				onArrowUp();
			}
		});

	const handleReplaceAllClick = () =>
		skipWhileComposing(() => {
			onReplaceAll({ replaceText });
			setIsHelperMessageVisible(true);
			if (count.totalReplaceable) {
				triggerSuccessReplacementMessageUpdate(count.totalReplaceable);
				setReplaceCount(count.totalReplaceable);
			} else {
				triggerSuccessReplacementMessageUpdate(count.total);
				setReplaceCount(count.total);
			}
			setFindTyped(false);
		});

	const handleCompositionStart = () => {
		setIsComposing(true);
	};

	const handleCompositionEnd = (event: React.CompositionEvent<HTMLInputElement>) => {
		setIsComposing(false);
		// type for React.CompositionEvent doesn't set type for target correctly
		updateReplaceValue((event.target as HTMLInputElement).value);
	};

	const clearSearch = () => {
		onCancel({ triggerMethod: TRIGGER_METHOD.BUTTON });
		focusToolbarButton();
	};

	const handleFindNextClick = () => {
		if (!isComposing) {
			onFindNext({ triggerMethod: TRIGGER_METHOD.BUTTON });
		}
	};

	const handleFindPrevClick = () => {
		if (!isComposing) {
			onFindPrev({ triggerMethod: TRIGGER_METHOD.BUTTON });
		}
	};

	const resultsReplace = formatMessage(messages.replaceSuccess, {
		numberOfMatches: replaceCount,
	});

	return (
		<Box xcss={replaceContainerStyles}>
			{allowReplace && (
				<Fragment>
					<Box xcss={replaceWithLabelStyle}>
						<Text
							id="replace-text-field-label"
							size="medium"
							weight="bold"
							color="color.text.subtle"
						>
							{replaceWith}
						</Text>
					</Box>
					<Textfield
						name="replace"
						aria-labelledby="replace-text-field-label"
						testId="replace-field"
						appearance="standard"
						defaultValue={replaceText}
						ref={replaceTextfieldRef}
						autoComplete="off"
						onChange={handleReplaceChange}
						onKeyDown={handleReplaceKeyDown}
						onCompositionStart={handleCompositionStart}
						onCompositionEnd={handleCompositionEnd}
					/>
					{isHelperMessageVisible && !findTyped && (
						<div ref={successReplacementMessageRef}>
							<ValidMessage testId="message-success-replacement">
								{fakeSuccessReplacementMessageUpdate
									? resultsReplace.replace(SPACE_REGEX, '\u00a0')
									: resultsReplace}
							</ValidMessage>
						</div>
					)}
				</Fragment>
			)}
			<Box xcss={allowReplace ? actionButtonContainerStyles : findOnlyActionButtonContainerStyles}>
				<Inline xcss={actionButtonParentInlineStyles}>
					<Inline xcss={actionButtonInlineStyles}>
						<FindReplaceTooltipButton
							title={formatMessage(messages.findNext)}
							// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
							icon={(iconProps) => <ChevronDownIcon label={iconProps.label} size="small" />}
							iconLabel={formatMessage(messages.findNext)}
							keymapDescription={'Enter'}
							onClick={handleFindNextClick}
							disabled={count.total <= 1}
						/>
						<FindReplaceTooltipButton
							title={findPrevious}
							// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
							icon={(iconProps) => <ChevronUpIcon label={iconProps.label} size="small" />}
							iconLabel={findPrevious}
							keymapDescription={'Shift Enter'}
							onClick={handleFindPrevClick}
							disabled={count.total <= 1}
						/>
						{allowReplace && (
							<Fragment>
								<Button
									testId={'Replace'}
									id="replace-button"
									onClick={handleReplaceClick}
									isDisabled={!canReplace}
								>
									{formatMessage(messages.replace)}
								</Button>
								<Button
									appearance="primary"
									testId={replaceAll}
									id="replaceAll-button"
									onClick={handleReplaceAllClick}
									isDisabled={count.totalReplaceable === 0}
								>
									{replaceAll}
								</Button>
							</Fragment>
						)}
					</Inline>
					<Inline xcss={closeButtonInlineStyles}>
						<Button appearance="subtle" testId={closeFindReplaceDialog} onClick={clearSearch}>
							{closeFindReplaceDialog}
						</Button>
					</Inline>
				</Inline>
			</Box>
		</Box>
	);
};

// eslint-disable-next-line @typescript-eslint/no-restricted-types
const _default_1: React.FC<WithIntlProps<ReplaceProps & WrappedComponentProps>> & {
	WrappedComponent: React.ComponentType<ReplaceProps & WrappedComponentProps>;
} = injectIntl(Replace);
export default _default_1;
